import { Alert, IconWithText, Link, Modal, TextInput } from '@pmi/dsm-react';
import { Formik, FormikHelpers } from 'formik';
import { Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { AlertWarningSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { ReactNode } from 'react';
import { User } from '../../../../../../shared/models/UserModels';
import { useUpdatePmiIdMutation } from './apiSlice';
import { accountSelector } from '../../../../../../shared/store/shared/accountSlice';
import { toastWithAnalytics } from '../../../../../../shared/analytics';
import styles from './styles.module.scss';
import {
  getTextForNoPersonId,
  getUserText,
} from '../../../../../../shared/helpers';

interface UpdatePmiIdModalProps {
  user: User;
  onCancel?: () => void;
}

interface FormModel {
  personId: number;
}

export default function UpdatePmiIdModal({
  user,
  onCancel = () => {},
}: UpdatePmiIdModalProps) {
  const { id, personName } = user;
  const { currentAccountId } = useSelector(accountSelector);
  const onCancelHandler = () => {
    onCancel();
  };

  const initialData: FormModel = {
    personId: undefined,
  };

  const validationSchema = Yup.object().shape({
    personId: Yup.number()
      .required('PMI ID is required')
      .integer('Must be a valid number')
      .typeError('Must be a valid number')
      .min(1, 'Must be a positive number')
      .test(
        'noEOrSign',
        'Must be a valid number',
        (value) =>
          typeof value === 'number' && !/[eE+-]/.test(value.toString()),
      ),
  });

  const [updatePmiId, updatedPmiId] = useUpdatePmiIdMutation();

  const errorMessage = ``;

  const onSubmit = async (
    values: FormModel,
    action: FormikHelpers<FormModel>,
  ) => {
    action.setSubmitting(true);
    updatePmiId({
      accountId: currentAccountId,
      userId: id,
      personId: values.personId,
    })
      .unwrap()
      .then(() => {
        const message = (): ReactNode => (
          <>
            You have updated <span data-exclude-lg>{getUserText(user)}</span>
          </>
        );
        toastWithAnalytics(message(), 'success');
        updatedPmiId.reset();
        action.resetForm();
        // onSuccess();
        // onCancel();
      })
      .catch((e) => {
        toastWithAnalytics(
          `${errorMessage} ${e.data?.detail?.toString()}`,
          'danger',
        );
        action.setSubmitting(false);
      })
      .finally(() => {
        action.setSubmitting(false);
      });
  };

  if (!user) return null;

  return (
    <Formik
      initialValues={initialData}
      validateOnChange
      validateOnBlur
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        submitForm,
        dirty,
        errors,
        handleChange,
        values,
        isValid,
        isSubmitting,
      }) => (
        <Modal
          show
          onClose={onCancelHandler}
          headline="Update PMI Id"
          variant="input"
          buttons={[
            {
              titleText: 'Cancel',
              variant: 'outline-secondary',
              onClick: onCancelHandler,
              disabled: isSubmitting,
            },
            {
              titleText: 'Update PMI ID',
              enforceTitleCase: false,
              variant: 'primary',
              onClick: submitForm,
              disabled: isSubmitting || !dirty || !isValid,
            },
          ]}
        >
          <>
            <Alert
              icon={AlertWarningSolidIcon}
              variant="warning"
              alertText="Please ensure the PMI ID is accurate before associating it to this
          email. Changing the PMI ID will not impact the emails found on
          profiles"
            />
            <div className="row align-items-start mt-3">
              <div className="col-6 mb-3">
                <h5 className="mb-2">{personName}</h5>
                <div
                  id="viewUserModal-personId"
                  className="d-flex align-items-center mb-2 flex-wrap"
                >
                  <span className={`${styles.textSecondary}`}>PMI ID: </span>
                  <strong
                    className={`ms-1 right-divider ${styles.textSecondary}`}
                  >
                    {user.personId
                      ? user.personId
                      : getTextForNoPersonId(user, true)}
                  </strong>
                </div>
                <div
                  id="viewUserModal-email"
                  className="d-flex align-items-center mb-2 flex-wrap me-5"
                >
                  <IconWithText
                    text="Email:"
                    className={styles.customIconWithText}
                  />
                  <Link
                    label={user.email}
                    href={`mailto:${user.email}`}
                    className={`ms-1 ${styles.monochrome} ${styles.textSecondary}`}
                  />
                </div>
              </div>
              <div className="col-6 mb-3">
                <Form.Group
                  controlId="personId"
                  className={errors?.personId ? 'is-invalid' : null}
                >
                  <Form.Label className="mb-2">New PMI ID</Form.Label>
                  <TextInput
                    name="personId"
                    maxLength={12}
                    onChange={handleChange}
                    value={values?.personId}
                    isInvalid={!!errors?.personId}
                    className={errors?.personId ? 'is-invalid' : null}
                    validationMessage={errors?.personId}
                  />
                </Form.Group>
              </div>
            </div>
          </>
        </Modal>
      )}
    </Formik>
  );
}
